import { useEffect, useRef } from "react";

export type DocumentCaptureEventName = "click" | "touchstart";

const DEFAULT_EVENTS: readonly DocumentCaptureEventName[] = [
  "click",
  "touchstart"
];

/**
 * Registers document-level listeners in the capture phase (same pattern as
 * outside-click / sidebar-dismiss). Keeps the latest handler via a ref so
 * listeners are not re-attached every render.
 */
export function useDocumentCapturePhaseListener(
  enabled: boolean,
  handler: (event: MouseEvent | TouchEvent) => void,
  eventNames: readonly DocumentCaptureEventName[] = DEFAULT_EVENTS
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const eventsRef = useRef(eventNames);
  eventsRef.current = eventNames;

  const eventsKey = eventNames.join("|");

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const names = eventsRef.current;
    const listener: EventListener = event => {
      handlerRef.current(event as MouseEvent | TouchEvent);
    };
    names.forEach(name => {
      document.addEventListener(name, listener, true);
    });
    return () => {
      names.forEach(name => {
        document.removeEventListener(name, listener, true);
      });
    };
  }, [enabled, eventsKey]);
}
